import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';
import { isSupabaseConfigured, isDemoModeForced } from '../../lib/supabase';
import { useCurrentUser } from '../../lib/currentUser';

const Sidebar = ({ isOpen = false, onClose, isCollapsed = false, onToggleCollapse }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const currentUser = useCurrentUser();

  // The district cheat sheet is Jennifer's private live-call playbook —
  // only real Supabase-authed owners should see it in the sidebar (or
  // the CheatSheetPage will show them the "Owner-only" placeholder even
  // if they URL-hack in). Anyone in demo mode or unauthenticated does
  // not get this nav entry at all.
  const isRealOwner = currentUser?.backend === 'supabase';

  // Stash the latest `onClose` in a ref so the route-change effect below
  // doesn't have to list `onClose` as a dep. Every parent page passes
  // `onClose` as an inline arrow function ("() => setIsSidebarOpen(false)"),
  // which gets a new reference on every parent render. If we listed it
  // as a dep, opening the sidebar on mobile would immediately re-fire
  // the effect and close it again — the hamburger button would appear
  // to do nothing. See regression: the mobile menu opens for one frame
  // and then vanishes.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  // Flat, single-column list of every page a teacher can go to. We
  // used to nest these under "My Students", "Reports & Insights",
  // "My Team" sections that had to be expanded — which meant a
  // first-time user opening the sidebar didn't SEE "IEP Goals" or
  // "Log Behavior" and thought the app didn't have those features.
  // For an older / lower-tech user in particular, "click a header
  // to reveal 3 more links" is a hidden UI pattern they wouldn't
  // discover. Flat wins. Ordering matches the teacher's daily flow:
  // Dashboard → look at students → look at their goals → log behavior
  // if something happened → reports / AI at review time → team +
  // resources at the bottom.
  const navigationItems = useMemo(() => [
    {
      id: 'dashboard',
      label: 'Home',
      icon: 'LayoutDashboard',
      path: '/dashboard',
      description: 'Your starting page.',
    },
    {
      id: 'student-profiles',
      label: 'Students',
      icon: 'Users',
      path: '/student-profile-management',
      description: 'Add students, edit profiles, print a one-pager.',
    },
    {
      id: 'iep-goals',
      label: 'IEP Goals',
      icon: 'Target',
      path: '/iep-goals-and-objectives-tracking',
      description: 'Write goals, log progress.',
    },
    {
      id: 'progress-tracker',
      label: 'Progress Tracker',
      icon: 'TrendingUp',
      path: '/progress-tracker',
      description: 'Who needs data this week — one view.',
    },
    {
      id: 'student-check-in',
      label: 'Student Check-In',
      icon: 'Hand',
      path: '/check-in',
      description: 'Hand the iPad to the student to self-rate their goals.',
    },
    {
      id: 'behavioral-incidents',
      label: 'Log Behavior',
      icon: 'AlertTriangle',
      path: '/behavioral-incident-logging',
      description: 'Record what happened, and why you think it did.',
    },
    {
      id: 'reports',
      label: 'Reports',
      icon: 'FileText',
      path: '/reports-and-analytics',
      description: 'Print or export for meetings.',
    },
    {
      id: 'paper-trackers',
      label: 'Paper Trackers',
      icon: 'Printer',
      path: '/paper-trackers',
      description: 'Print a blank sheet to fill in by hand.',
    },
    {
      id: 'ai-insights',
      label: 'AI Insights',
      icon: 'Brain',
      path: '/ai-powered-insights-and-recommendations',
      description: "What your data is showing.",
    },
    {
      id: 'collaboration-hub',
      label: 'My Team',
      icon: 'Users2',
      path: '/multi-user-collaboration-hub',
      description: 'Teacher, para, SLP, OT, PT.',
    },
    {
      id: 'pbis-resources',
      label: 'Behavior Toolkit',
      icon: 'BookOpen',
      path: '/pbis-resources',
      description: 'Behavior strategies & reference cards.',
    },
    {
      id: 'help-and-manual',
      label: 'Help',
      icon: 'HelpCircle',
      path: '/help',
      description: 'How to use the app.',
    },
    // Owner-only: private district Q&A playbook. Filtered out below for
    // demo / unauthenticated users so it doesn't leak in the demo.
    ...(isRealOwner
      ? [{
          id: 'cheat-sheet',
          label: 'District Cheat Sheet',
          icon: 'BookMarked',
          path: '/cheat-sheet',
          description: 'Your live-call playbook (owner only).',
          ownerOnly: true,
        }]
      : []),
  ], [isRealOwner]);

  const handleNavigation = (path) => {
    navigate(path);
    if (window.innerWidth < 1024) {
      onClose?.();
    }
  };

  const isActiveRoute = (path) => {
    return location?.pathname === path;
  };

  // Close sidebar on mobile when route changes. We intentionally only
  // depend on the pathname — see the ref indirection above for why
  // `onClose` cannot be in this dep array.
  useEffect(() => {
    if (window.innerWidth < 1024) {
      onCloseRef.current?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location?.pathname]);

  const sidebarClasses = `
    fixed left-0 top-16 bottom-0 z-[900] bg-surface border-r border-border shadow-soft
    transition-all duration-300 ease-in-out
    ${isCollapsed ? 'w-16' : 'w-72'}
    ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
  `;

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-[850] lg:hidden"
          onClick={onClose}
        />
      )}
      {/* Sidebar */}
      <aside className={sidebarClasses}>
        <div className="flex flex-col h-full">
          {/* Logo Section */}
          <div className="flex items-center justify-between p-4 border-b border-border">
            {!isCollapsed && (
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                  <Icon name="GraduationCap" size={20} className="text-primary-foreground" />
                </div>
                <div>
                  <h1 className="font-semibold text-foreground">EduTracker Pro</h1>
                  <p className="text-xs text-muted-foreground">Special Education Platform</p>
                </div>
              </div>
            )}
            
            {/* Collapse Toggle - Desktop Only */}
            <Button
              variant="ghost"
              size="icon"
              onClick={onToggleCollapse}
              className="hidden lg:flex"
              aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              <Icon name={isCollapsed ? 'ChevronRight' : 'ChevronLeft'} size={16} />
            </Button>
          </div>

          {/* Navigation. Flat list — every page is one tap away, no
              collapsible sections. Each item is a big row with the
              icon, the page name, and a one-line "what's this?"
              description under it. When the sidebar is collapsed (an
              opt-in desktop mode), we show icon-only with a tooltip.
              */}
          <nav className="flex-1 overflow-y-auto p-4">
            <div className="space-y-1">
              {navigationItems?.map((item) => {
                const isActive = isActiveRoute(item?.path);
                return (
                  <Button
                    key={item?.id}
                    variant={isActive ? 'default' : 'ghost'}
                    onClick={() => handleNavigation(item?.path)}
                    className={`w-full justify-start gap-3 h-auto py-2.5 ${
                      isCollapsed ? 'px-3' : 'px-4'
                    }`}
                    title={isCollapsed ? item?.label : undefined}
                  >
                    <Icon
                      name={item?.icon}
                      size={20}
                      className="flex-shrink-0"
                    />
                    {!isCollapsed && (
                      <div className="flex-1 text-left min-w-0">
                        <div className="font-medium text-sm leading-tight">
                          {item?.label}
                        </div>
                        {!isActive && item?.description && (
                          <div className="text-xs text-muted-foreground line-clamp-1 mt-0.5">
                            {item.description}
                          </div>
                        )}
                      </div>
                    )}
                  </Button>
                );
              })}
            </div>
          </nav>

          {/* Footer — shows a "Go live" call-to-action when the app is
              not fully configured (or is running in forced-demo mode).
              Once the customer is live, the CTA hides and only the tiny
              copyright/version stays. Keeps the footer useful without
              nagging real users. */}
          <div className="p-4 border-t border-border space-y-3">
            {(!isSupabaseConfigured() || isDemoModeForced()) && (
              <Button
                variant="warning"
                size={isCollapsed ? 'icon' : 'default'}
                iconName="Rocket"
                iconPosition="left"
                onClick={() => handleNavigation('/go-live')}
                className="w-full justify-start"
                title={isCollapsed ? 'Go live' : undefined}
                aria-label="Go live"
              >
                {!isCollapsed && 'Go live'}
              </Button>
            )}
            {!isCollapsed && (
              <div className="text-xs text-muted-foreground">
                <p>© 2025 EduTracker Pro</p>
                <p>Version 2.1.0</p>
              </div>
            )}
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;